import { FC, useState, useEffect } from 'react';
import { Card, List, Tag, Button, Empty, Spin, Tabs, Badge, Space, Typography, Modal, Pagination, Select } from 'antd';
import { BellOutlined, CheckOutlined, EyeOutlined, RightOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { notificationsAPI } from '../../services/notifications/notifications.api';
import { TNotification, NotificationType } from '../../types/notification';
import { useAuth } from '../../store';
import PageMeta from '../../components/common/meta';
import { useNavigate } from 'react-router-dom';
import { formatMentions } from '../../utils/formatMentions';

dayjs.extend(relativeTime);

const { TabPane } = Tabs;
const { Text } = Typography;
const { Option } = Select;

export const ScreenStaffNotifications: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<TNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('unread');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNotification, setSelectedNotification] = useState<TNotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filterType, setFilterType] = useState<NotificationType | undefined>(undefined);
  const pageSize = 10;

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      fetchUnreadCount();
      fetchTotalCount();
    }
  }, [user?.id, activeTab, currentPage, filterType]);

  useEffect(() => {
    // Reset to page 1 when switching tabs or filter
    setCurrentPage(1);
  }, [activeTab, filterType]);

  const fetchNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      // Calculate date 14 days ago
      const fourteenDaysAgo = dayjs().subtract(14, 'days').toISOString();

      const response = await notificationsAPI.getNotifications({
        userId: user.id,
        page: currentPage,
        pageSize,
        isRead: activeTab === 'unread' ? false : undefined,
        startDate: fourteenDaysAgo,
        notificationType: filterType,
      });
      setNotifications(response.items);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!user?.id) return;

    try {
      const count = await notificationsAPI.getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchTotalCount = async () => {
    if (!user?.id) return;

    try {
      const fourteenDaysAgo = dayjs().subtract(14, 'days').toISOString();

      // Always fetch total count for "All Notifications" tab label
      const response = await notificationsAPI.getNotifications({
        userId: user.id,
        page: 1,
        pageSize: 1, // We only need the count, not the items
        startDate: fourteenDaysAgo,
        notificationType: filterType,
      });
      setTotalCount(response.meta.totalItems);
    } catch (error) {
      console.error('Failed to fetch total count:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    try {
      await notificationsAPI.markAllAsRead(user.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getTypeColor = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.MENTION:
        return 'blue';
      case NotificationType.JOB_ASSIGNMENT:
        return 'orange';
      case NotificationType.SERVICE_TICKET_ASSIGNMENT:
        return 'purple';
      case NotificationType.TASK_ASSIGNMENT:
        return 'green';
      case NotificationType.TASK_UPDATE:
        return 'cyan';
      case NotificationType.JOB_UPDATE:
        return 'magenta';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.MENTION:
        return 'Mention';
      case NotificationType.JOB_ASSIGNMENT:
        return 'Job Assignment';
      case NotificationType.SERVICE_TICKET_ASSIGNMENT:
        return 'Service';
      case NotificationType.TASK_ASSIGNMENT:
        return 'Task';
      case NotificationType.TASK_UPDATE:
        return 'Update';
      case NotificationType.JOB_UPDATE:
        return 'Update';
      default:
        return 'Info';
    }
  };

  const handleNotificationClick = async (notification: TNotification) => {
    // Mark as read when clicked
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    // Always show detail modal
    setSelectedNotification(notification);
    setShowDetailModal(true);
  };

  const handleGoToAssignment = (notification: TNotification) => {
    if (!notification.serviceTicket) return;

    // Get the service date to navigate to the correct date
    const serviceDate = notification.serviceTicket.serviceDatetime;
    const serviceTicketId = notification.serviceTicketId;

    // Navigate to assignments with query params for date and service ticket ID
    const params = new URLSearchParams();
    if (serviceDate) {
      params.set('date', new Date(serviceDate).toISOString().split('T')[0]);
    }
    if (serviceTicketId) {
      params.set('serviceTicketId', serviceTicketId);
    }

    navigate(`/assignments?${params.toString()}`);
    setShowDetailModal(false);
  };

  return (
    <>
      <PageMeta title="Notifications | Staff Portal" description="Your notifications" />
      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        <Card>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <div className="flex items-center gap-3">
              <BellOutlined className="text-2xl text-blue-500" />
              <div>
                <h1 className="text-xl md:text-2xl font-semibold m-0">
                  Notifications
                </h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-500 m-0">
                    You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleMarkAllAsRead}
                size="small"
              >
                Mark All Read
              </Button>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'all' | 'unread')}
            items={[
              {
                key: 'unread',
                label: `Unread (${unreadCount})`,
              },
              {
                key: 'all',
                label: `All Notifications (${totalCount})`,
              },
            ]}
          />

          <div className="mb-4">
            <Space>
              <FilterOutlined />
              <Text strong>Filter by Type:</Text>
              <Select
                style={{ width: 200 }}
                placeholder="All Types"
                allowClear
                value={filterType}
                onChange={(value) => setFilterType(value)}
              >
                <Option value={NotificationType.MENTION}>Mentions</Option>
                <Option value={NotificationType.SERVICE_TICKET_ASSIGNMENT}>Service Assignments</Option>
                <Option value={NotificationType.TASK_ASSIGNMENT}>Task Assignments</Option>
                <Option value={NotificationType.TASK_UPDATE}>Task Updates</Option>
                <Option value={NotificationType.JOB_UPDATE}>Job Updates</Option>
              </Select>
            </Space>
          </div>

          <Spin spinning={loading}>
            {notifications.length === 0 ? (
              <Empty
                description={
                  activeTab === 'unread'
                    ? 'No unread notifications'
                    : 'No notifications yet'
                }
                className="my-8"
              />
            ) : (
              <List
                itemLayout="vertical"
                dataSource={notifications}
                renderItem={(notification) => (
                  <List.Item
                    key={notification.id}
                    className={`${
                      !notification.isRead ? 'bg-blue-50' : ''
                    } rounded-lg px-3 py-2 mb-2 cursor-pointer hover:bg-gray-50 transition-colors`}
                    onClick={() => handleNotificationClick(notification)}
                    actions={[
                      !notification.isRead && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                        >
                          Mark as read
                        </Button>
                      ),
                      <Button
                        type="text"
                        size="small"
                        icon={<RightOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNotificationClick(notification);
                        }}
                      >
                        View
                      </Button>
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      avatar={
                        !notification.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                        )
                      }
                      title={
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <span className="font-semibold text-sm md:text-base">
                            {notification.title}
                          </span>
                          <Space size="small">
                            <Tag color={getTypeColor(notification.notificationType)}>
                              {getTypeLabel(notification.notificationType)}
                            </Tag>
                            <span className="text-xs text-gray-500">
                              {dayjs(notification.createdAt).fromNow()}
                            </span>
                          </Space>
                        </div>
                      }
                      description={
                        <div className="mt-2">
                          <p className="text-sm text-gray-700 m-0">
                            {formatMentions(notification.content)}
                          </p>
                          {notification.triggeredByUser && (
                            <p className="text-xs text-gray-500 mt-2 m-0">
                              from {notification.triggeredByUser.firstName}{' '}
                              {notification.triggeredByUser.lastName}
                            </p>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            {totalCount > pageSize && (
              <div className="mt-4 flex justify-center">
                <Pagination
                  current={currentPage}
                  total={totalCount}
                  pageSize={pageSize}
                  onChange={(page) => setCurrentPage(page)}
                  showSizeChanger={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} notifications (last 14 days)`}
                />
              </div>
            )}
          </Spin>
        </Card>
      </div>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <Tag color={getTypeColor(selectedNotification?.notificationType || NotificationType.GENERAL)}>
              {getTypeLabel(selectedNotification?.notificationType || NotificationType.GENERAL)}
            </Tag>
            <span>{selectedNotification?.title}</span>
          </Space>
        }
        open={showDetailModal}
        onCancel={() => {
          setShowDetailModal(false);
          setSelectedNotification(null);
        }}
        footer={[
          <Button key="close" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>,
          selectedNotification?.serviceTicketId && (
            <Button
              key="view-service"
              type="primary"
              onClick={() => handleGoToAssignment(selectedNotification)}
            >
              View Service Ticket
            </Button>
          ),
        ].filter(Boolean)}
        width={700}
      >
        {selectedNotification && (
          <div>
            {/* Metadata Section */}
            {selectedNotification.metadata && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {selectedNotification.metadata.jobTicketRef && (
                    <div>
                      <Text strong>Job Ticket:</Text> {selectedNotification.metadata.jobTicketRef}
                    </div>
                  )}
                  {selectedNotification.metadata.serviceTicketRef && (
                    <div>
                      <Text strong>Service Ticket:</Text> {selectedNotification.metadata.serviceTicketRef}
                    </div>
                  )}
                  {selectedNotification.metadata.taskRef && (
                    <div>
                      <Text strong>Task:</Text> {selectedNotification.metadata.taskRef}
                    </div>
                  )}
                  {selectedNotification.metadata.clientName && (
                    <div>
                      <Text strong>Client:</Text> {selectedNotification.metadata.clientName}
                    </div>
                  )}
                  {selectedNotification.metadata.serviceType && (
                    <div>
                      <Text strong>Service Type:</Text> {selectedNotification.metadata.serviceType}
                    </div>
                  )}
                </Space>
              </div>
            )}

            {/* Note Content */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Message:</Text>
              <div style={{
                padding: 12,
                background: '#fafafa',
                borderRadius: 4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {formatMentions(selectedNotification.content)}
              </div>
            </div>

            {/* Footer Info */}
            <div style={{ paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {selectedNotification.triggeredByUser && (
                  <Text type="secondary">
                    From: {selectedNotification.triggeredByUser.firstName}{' '}
                    {selectedNotification.triggeredByUser.lastName}
                  </Text>
                )}
                <Text type="secondary">
                  {dayjs(selectedNotification.createdAt).format('MMM D, YYYY [at] h:mm A')}
                  {' '}({dayjs(selectedNotification.createdAt).fromNow()})
                </Text>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
